import { Link, useRouterState } from "@tanstack/react-router"
import { useEffect, useState } from "react"

import { AppleMark, PlayMark } from "@/components/icon/StoreMarks"
import { APP_STORE_URL, APP_URL, externalLinkProps, GOOGLE_PLAY_URL } from "@/constants/links"

const STORE_LINKS = [
	{
		label: "iOS",
		ariaLabel: "Download for iOS on the App Store",
		href: APP_STORE_URL,
		Mark: AppleMark,
	},
	{
		label: "Android",
		ariaLabel: "Download for Android on Google Play",
		href: GOOGLE_PLAY_URL,
		Mark: PlayMark,
	},
]

const NAV_LINKS = [
	{ label: "About", id: "about" },
	{ label: "Services", id: "services" },
	{ label: "How It Works", id: "how-it-works" },
	{ label: "Why Use It", id: "why-use" },
	{ label: "FAQ", id: "faq" },
	{ label: "Contact", id: "contact" },
]

/**
 * Highlights the nav item for the section currently in view. Only meaningful
 * on "/" — the legal pages have no matching section ids, so the observer
 * simply never fires there and every link stays inactive.
 */
function useActiveSection(enabled: boolean) {
	const [activeId, setActiveId] = useState<string | null>(null)

	useEffect(() => {
		if (!enabled) return

		const observer = new IntersectionObserver(
			entries => {
				for (const entry of entries) {
					if (entry.isIntersecting) setActiveId(entry.target.id)
				}
			},
			{ rootMargin: "-40% 0px -55% 0px" },
		)

		for (const link of NAV_LINKS) {
			const node = document.getElementById(link.id)
			if (node) observer.observe(node)
		}
		return () => observer.disconnect()
	}, [enabled])

	return activeId
}

/** True once the page has scrolled past the point the header would otherwise overlap. */
function useScrolled(threshold = 8) {
	const [scrolled, setScrolled] = useState(false)

	useEffect(() => {
		function onScroll() {
			setScrolled(window.scrollY > threshold)
		}
		onScroll()
		window.addEventListener("scroll", onScroll, { passive: true })
		return () => window.removeEventListener("scroll", onScroll)
	}, [threshold])

	return scrolled
}

export function Header() {
	const pathname = useRouterState({ select: s => s.location.pathname })
	const isHome = pathname === "/"
	const activeId = useActiveSection(isHome)
	const scrolled = useScrolled()

	return (
		<header
			className={`fixed top-0 right-0 left-0 z-50 border-b transition-all duration-300 ${
				scrolled
					? "border-hairline/40 bg-surface/40 shadow-sm backdrop-blur-xl"
					: "border-hairline bg-surface-container-low backdrop-blur-none"
			}`}
		>
			<div className="gap-space-md sm:px-margin mx-auto flex h-24 max-w-7xl items-center justify-between px-4 xl:px-0">
				<div className="gap-space-md flex items-center">
					<Link
						to="/"
						className="group flex items-center"
					>
						{/* <span className="flex flex-col items-center transition-transform group-hover:scale-[1.02]">
							<span className="text-headline-md lg:text-headline-lg text-primary font-brand leading-tight font-normal tracking-wide uppercase">
								Swamithra
							</span>
							<span className="text-on-surface-variant text-label-sm font-brand-tagline hidden font-bold whitespace-nowrap uppercase italic sm:block">
								…Your Partner in Direct Tax Matters
							</span>
						</span> */}
						<img
							src="/images/logo.png"
							alt="logo"
							className="h-20 w-auto sm:hidden"
						/>

						<img
							src="/images/logo-bg.png"
							height={302}
							width={826}
							alt="logo"
							className="hidden h-24 w-auto sm:block"
						/>
					</Link>
				</div>

				<nav className="hidden items-center gap-7 lg:flex">
					{NAV_LINKS.map(link => {
						const isActive = isHome && activeId === link.id
						return (
							<a
								key={link.id}
								href={`/#${link.id}`}
								className={`text-label-lg group relative pb-1 font-sans transition-colors ${
									isActive
										? "text-primary font-semibold"
										: "text-on-surface-variant hover:text-on-surface"
								}`}
							>
								{link.label}
								<span
									className={`bg-primary absolute inset-x-0 -bottom-px h-0.5 origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100 ${
										isActive ? "scale-x-100" : ""
									}`}
								/>
							</a>
						)
					})}
				</nav>

				<div className="flex items-center gap-2 sm:gap-4">
					<div className="flex items-center gap-1.5">
						<span className="text-label-md text-on-surface-variant mr-1 hidden font-sans xl:inline">
							Get the app
						</span>
						{STORE_LINKS.map(store => (
							<a
								key={store.label}
								href={store.href}
								{...externalLinkProps}
								aria-label={store.ariaLabel}
								className="border-hairline text-label-md text-on-surface hover:bg-surface-container focus-visible:outline-primary inline-flex size-8 items-center justify-center gap-1.5 rounded-lg border font-sans transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 md:size-auto md:px-3 md:py-2"
							>
								<store.Mark className="size-4" />
								<span className="hidden md:inline">{store.label}</span>
							</a>
						))}
					</div>
					<a
						href={APP_URL}
						{...externalLinkProps}
						className="bg-primary-container text-label-md text-on-primary hover:bg-primary focus-visible:outline-primary inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 font-sans font-medium whitespace-nowrap shadow-sm transition-all hover:shadow focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-[0.99] sm:px-5"
					>
						<span>Login</span>
						<span className="bg-on-primary/15 text-label-sm rounded-full px-2 py-0.5 font-sans font-semibold sm:inline">
							Free Help
						</span>
					</a>
				</div>
			</div>
		</header>
	)
}
